import { ArrowRight } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { wa, WA_MESSAGES, IMG } from "@/lib/site";

const FACTS = [
  {
    title: "What “A2” refers to",
    text: "Cow milk naturally contains a protein called beta-casein. Indigenous Indian breeds like the Gir produce milk with the A2 type of this protein — the same type traditionally consumed in India for generations.",
  },
  {
    title: "What Gir cows are",
    text: "The Gir is an indigenous desi breed from the Gir forest region of Gujarat, treasured in India for centuries and known for its rich, flavourful milk.",
  },
  {
    title: "A place in Indian tradition",
    text: "Ghee has held a central place in Indian kitchens and rituals for thousands of years — from everyday dal to festive sweets.",
  },
  {
    title: "How ghee is commonly used",
    text: "For tempering (tadka), sautéing, finishing dals and curries, spreading on rotis, and enriching traditional sweets.",
  },
  {
    title: "Taste & aroma",
    text: "Good ghee is golden, grainy and deeply aromatic, with a warm, nutty richness that lifts simple food.",
  },
  {
    title: "Cooking applications",
    text: "Ghee's naturally high smoke point makes it a favourite for Indian cooking — from slow simmering to high-heat tempering.",
  },
];

export default function Education() {
  return (
    <section id="education" data-testid="education-section" className="bg-cream py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <SectionHeading eyebrow="Know Your Ghee" title="Why A2 Gir Cow Ghee?" />
              <Reveal delay={0.15}>
                <div className="group mt-10 overflow-hidden rounded-[28px] shadow-[0_40px_80px_-35px_rgba(26,54,38,0.45)]">
                  <img
                    data-testid="education-cow-image"
                    src={IMG.cow}
                    alt="A Gir cow, an indigenous Indian breed"
                    loading="lazy"
                    className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
              </Reveal>
            </div>
          </div>

          <div className="lg:col-span-7 lg:pl-6">
            <div className="flex flex-col divide-y divide-forest/10">
              {FACTS.map((f, i) => (
                <Reveal key={f.title} delay={0.05 * i}>
                  <div
                    data-testid={`education-fact-${i + 1}`}
                    className="group flex gap-6 py-7 first:pt-0"
                  >
                    <span className="font-heading text-3xl text-gold/60 transition-colors duration-500 group-hover:text-gold">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <h3 className="font-heading text-2xl text-forest">{f.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-charcoal/65 md:text-base">
                        {f.text}
                      </p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <a
                data-testid="education-explore-cta"
                href={wa(WA_MESSAGES.pricing)}
                target="_blank"
                rel="noreferrer"
                className="group mt-6 inline-flex items-center gap-3 rounded-full bg-forest px-8 py-4 text-[12px] font-semibold uppercase tracking-[0.18em] text-ivory transition-[background-color,transform] duration-300 hover:-translate-y-1 hover:bg-forest-light"
              >
                Explore A2 Ghee
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
